import { getChartModule } from '../domain/catalog';
import type {
  ChartInput,
  ChartResult,
  EvidenceItem,
  ResultFact,
  ResultSection
} from '../domain/types';

const evidenceByModule: Record<ChartInput['moduleId'], EvidenceItem[]> = {
  liuyao: [
    { source: '《京氏易传》《火珠林》', rule: '京房八宫、纳甲与世应结构' },
    { source: '《增删卜易》《卜筮正宗》', rule: '动变、旬空与六亲关系' }
  ],
  meihua: [
    { source: '通行本《梅花易数》', rule: '时间或数字起卦、体用互变' },
    { source: '邵氏心易传统', rule: '以体为主，观察用、互、变的五行关系' }
  ],
  qimen: [
    { source: '《烟波钓叟歌》', rule: '九宫、九星、八门与八神排布' },
    { source: '《遁甲演义》', rule: '阴阳遁、局数和值符值使' }
  ],
  liuren: [
    { source: '《六壬大全》', rule: '天地盘、四课、三传与九宗门' },
    { source: '《大六壬指南》', rule: '课体与传课结构' }
  ],
  xiaoliuren: [
    { source: '小六壬掌诀通行法', rule: '月、日、时逐宫顺数' },
    { source: '算法口径说明', rule: '东八区民用日零点换日，闰月沿用同名月序' }
  ],
  taiyi: [
    { source: '《太乙金镜式经》', rule: '太乙积数、阴阳遁与宫位' },
    { source: '太乙统宗年家模型', rule: '七十二局基础盘' }
  ],
  bazi: [
    { source: '《渊海子平》《三命通会》', rule: '四柱、十神与格局基础' },
    { source: '《滴天髓》《穷通宝鉴》', rule: '旺衰、调候与取用提示' }
  ],
  ziwei: [
    { source: '《紫微斗数全书》', rule: '十二宫与星曜安置框架' },
    { source: 'iztro 开源排盘模型', rule: '星盘与运限结构化计算', note: 'MIT 许可证' }
  ],
  jinkoujue: [
    { source: '《六壬神课金口诀古本》', rule: '地分、将神、贵神、人元四位' },
    { source: '五子元遁与贵神起例', rule: '阴阳次第五用、五动与三动' }
  ],
  huangli: [
    { source: '《协纪辨方书》', rule: '建除、神煞和传统宜忌资料' },
    { source: '现代公历换算', rule: '指定日期的历法结构化整理' }
  ],
  'wuyun-liuqi': [
    { source: '《素问·天元纪大论》', rule: '天干化五运、地支配司天' },
    { source: '《素问·六微旨大论》', rule: '司天、在泉与六气位置关系' }
  ],
  'huangji-jingshi': [
    { source: '《皇极经世》', rule: '元、会、运、世周期框架' },
    { source: '蔡元定《皇极经世指要》', rule: '一元消长之数的传统整理' }
  ]
};

export function parseChartDate(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) {
    throw new Error('日期时间无效，请重新选择。');
  }
  const [, yearText, monthText, dayText, hourText, minuteText, secondText = '0'] = match;
  const [year, month, day, hour, minute, second] = [yearText, monthText, dayText, hourText, minuteText, secondText].map(Number);
  const date = new Date(0);
  date.setFullYear(year, month - 1, day);
  date.setHours(hour, minute, second, 0);
  if (
    Number.isNaN(date.getTime())
    || date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
    || date.getHours() !== hour
    || date.getMinutes() !== minute
    || date.getSeconds() !== second
  ) {
    throw new Error('日期时间无效，请重新选择。');
  }
  return date;
}

export function dateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function replaceLocalDateParts(
  source: Date,
  parts: { year: number; month: number; day: number }
): Date {
  return new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    source.getHours(),
    source.getMinutes(),
    source.getSeconds(),
    source.getMilliseconds()
  );
}

export function traditionalTimeIndex(date: Date): number {
  const hour = date.getHours();
  return hour === 23 ? 12 : Math.floor((hour + 1) / 2);
}

export function fact(label: string, value: unknown, fallback = '未见'): ResultFact {
  return { label, value: displayText(value, fallback) };
}

export function displayText(value: unknown, fallback = '未见'): string {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => displayText(item, '')).filter(Boolean).join('、') || fallback;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return fallback;
  }
}

export function section(title: string, facts: ResultFact[]): ResultSection {
  return { title, facts: facts.filter((item) => item.value !== '') };
}

export function buildChartResult(params: {
  input: ChartInput;
  date: Date;
  headline: string;
  summary: ResultFact[];
  sections: ResultSection[];
  raw: unknown;
  warnings?: string[];
}): ChartResult {
  const module = getChartModule(params.input.moduleId);
  if (!module) throw new Error('未找到所选术数模块。');

  return {
    moduleId: params.input.moduleId,
    moduleName: module.name,
    generatedAt: params.date.toISOString(),
    question: params.input.question?.trim() || undefined,
    headline: params.headline,
    summary: params.summary,
    sections: params.sections,
    evidence: evidenceByModule[params.input.moduleId],
    warnings: params.warnings ?? [],
    raw: params.raw
  };
}

export function requireGender(gender: ChartInput['gender']): 'male' | 'female' {
  if (gender !== 'male' && gender !== 'female') {
    throw new Error('八字和紫微排盘需要选择生理性别。');
  }
  return gender;
}

export function calculationQuestion(question?: string): string {
  return question?.trim() || '按当前盘面作概览。';
}
